import { themes } from '@blackbird/ui-base';
import I18Next from '@blackbird/ui-base/components/i18next';
import VersionContextWrapper from '@blackbird/ui-base/contexts/version';
import { CssBaseline } from '@material-ui/core';
import { createGenerateClassName, StylesProvider, ThemeProvider } from '@material-ui/styles';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { FC, useMemo, useEffect, StrictMode } from 'react';
import Rollbar from 'rollbar';

import PageLayout from '@/components/layout/page-layout';

const App: FC<AppProps> = ({ Component, pageProps = {} }) => {
  const generateClassName = useMemo(() => {
    return createGenerateClassName({
      disableGlobal: process.env.NODE_ENV === 'production',
      productionPrefix: 'bb',
    });
  }, []);

  useEffect(() => {
    new Rollbar({
      accessToken: process.env.ROLLBAR_CLIENT_TOKEN,
      captureUncaught: true,
      captureUnhandledRejections: true,
      enabled: process.env.NODE_ENV === 'production',
      payload: {
        environment: process.env.NODE_ENV,
        client: {
          javascript: {
            source_map_enabled: true,
            code_version: process.env.GIT_REVISION,
            guess_uncaught_frames: true,
          },
        },
      },
    });

    const jssStyles = document.querySelector('#jss');
    if (jssStyles) {
      jssStyles.parentElement?.removeChild(jssStyles);
    }
  }, []);

  return (
    <StylesProvider generateClassName={generateClassName}>
      <CssBaseline />
      <Head>
        <title>Blackbird - Smart Data Collection</title>
      </Head>
      <ThemeProvider theme={themes.light}>
        <VersionContextWrapper>
          <PageLayout>
            <Component {...pageProps} />
          </PageLayout>
        </VersionContextWrapper>
      </ThemeProvider>
    </StylesProvider>
  );
};

const WrappedApp = I18Next(App);

// @ts-ignore
WrappedApp.getInitialProps = async ({ Component }) => ({
  pageProps: Component.getInitialProps ? (await Component.getInitialProps()).props : {},
});

export default WrappedApp;
